import * as RadixDialog from '@radix-ui/react-dialog'
import { AnimatePresence } from 'motion/react'
import React, { ComponentType, useEffect, useState } from 'react'

import { Modal } from '@components/modal.component'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

export type ModalSize = 'sm' | 'md' | 'lg'

export type ModalConfig = {
  hideClose?: boolean
  size?: ModalSize
}

export type ModalResolveResult<T = Record<string, unknown>> =
  | ({ resolveType: 'resolve' } & T)
  | { resolveType: 'reject' }

export type ModalComponentProps = {
  onResolve: (data?: Record<string, unknown>) => void
  onReject: () => void
}

type ModalStateEntry = {
   
  Component: ComponentType<any>
  onReject: () => void
  onResolve: (data?: Record<string, unknown>) => void
   
  data: Record<string, any>
  config: ModalConfig
}

type ModalState = {
  modal: ModalStateEntry | null
}

export const useModalState = create<ModalState>()(
  subscribeWithSelector(() => ({
    modal: null as ModalStateEntry | null,
  })),
)

export function ModalEntry(): React.ReactElement {
  const [modal, setModal] = useState<ModalStateEntry | null>(null)
  useEffect(() => {
    return useModalState.subscribe(
      state => state.modal,
      state => {
        setModal(state)
      },
    )
  }, [])

  return (
    <RadixDialog.Root
      open={!!modal}
      onOpenChange={open => {
        if (!open) modal?.onReject()
      }}
    >
      <AnimatePresence>
        {!!modal && (
          <RadixDialog.Portal forceMount>
            <Modal
              onClose={modal.onReject}
              hideClose={modal.config?.hideClose}
              {...modal.config}
            >
              <modal.Component
                {...modal.data}
                onResolve={modal.onResolve}
                onReject={modal.onReject}
              />
            </Modal>
          </RadixDialog.Portal>
        )}
      </AnimatePresence>
    </RadixDialog.Root>
  )
}

class ModalService {
  __close = (): void => {
    useModalState.setState({ modal: null })
  }

   
  render<T extends Record<string, any> = Record<string, any>>(
     
    Component: ComponentType<any>,
    data: T = {} as T,
    config: ModalConfig = {},
     
  ): Promise<ModalResolveResult<any>> {
    let onResolve: (data?: Record<string, unknown>) => void
    let onReject: () => void

     
    const promise = new Promise<ModalResolveResult<any>>(resolve => {
      onResolve = (data?: Record<string, unknown>) => {
        resolve({ resolveType: 'resolve', ...data })
        this.__close()
      }
      onReject = () => {
        resolve({ resolveType: 'reject' })
        this.__close()
      }
    })

    useModalState.setState({
      modal: {
        Component,
        onReject: onReject!,
        onResolve: onResolve!,
        data,
        config,
      },
    })

    return promise
  }
}

export const modalService = new ModalService()
